/**
 * GFG-388 (Collections Settings, GFG Admin). React Query hooks for `/admin/collections` and the
 * custom-collection assignment routes. Every query hangs off one prefix; no mutation is
 * optimistic: each invalidates the prefix on success only, so a refused write leaves the UI as it was.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignCustomProduct,
  fetchAdminCollectionProducts,
  fetchAdminCollections,
  setCollectionPlatformEnabled,
  unassignCustomProduct,
} from "@/api/adminCollections";
import type { AssignCustomProductBody } from "@/interfaces/adminCollections";

export const ADMIN_COLLECTIONS_KEY = ["admin", "collections"];

/** GET /admin/collections. */
export function useAdminCollections() {
  return useQuery({
    queryKey: [...ADMIN_COLLECTIONS_KEY, "list"],
    queryFn: fetchAdminCollections,
  });
}

/** GET /admin/collections/:id/products -- only fetches once an id is known. */
export function useAdminCollectionProducts(id: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_COLLECTIONS_KEY, "products", id],
    queryFn: () => fetchAdminCollectionProducts(id as string),
    enabled: !!id,
  });
}

/** PATCH the platform switch. */
export function useSetPlatformEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setCollectionPlatformEnabled(id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_COLLECTIONS_KEY }),
  });
}

/** POST an organization + price tier onto a Custom Collection product. */
export function useAssignCustomProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: AssignCustomProductBody }) =>
      assignCustomProduct(productId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_COLLECTIONS_KEY }),
  });
}

/** DELETE a Custom Collection product's assignment. */
export function useUnassignCustomProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => unassignCustomProduct(productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_COLLECTIONS_KEY }),
  });
}
